/**
 * The hairline-bordered surface everything in this console sits on, in two
 * forms.
 *
 * `Panel` is the labelled container: a header row with a title and its own
 * actions, a body, an optional footer. `Card` is the bare surface with padding
 * and nothing else. They are one file because they are one idea at two levels
 * of ceremony, and splitting them would mean two definitions of the border, the
 * radius and the raised surface that could then drift apart.
 *
 * A hairline and a raised surface rather than a shadow. `tokens.css` has four
 * elevation steps and they are for things that genuinely float: menus, sheets,
 * toasts. A dashboard of nine shadowed cards is nine things claiming to hover
 * over a page that is not underneath them, and the shadows stack into visual
 * noise at exactly the density this product is built for.
 */

import { useId } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import styles from './Panel.module.css'

/*
 * `title` is omitted from the inherited attributes and redeclared. The DOM's
 * own `title` is the browser tooltip and is a string; this one is the panel's
 * visible heading and takes a node, so a status badge can sit inside it. They
 * cannot both be called `title`, and the visible heading is the one a caller of
 * a layout component means. A tooltip on a panel would be an accessibility
 * problem anyway: it never appears for a keyboard or a touch user.
 */
export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Renders the header row. Without it the panel is just a bordered box. */
  title?: ReactNode
  /**
   * Supporting prose under the title. Kept, and no longer passed by the
   * ordinary screens: a panel heading followed by a sentence describing the
   * table beneath it was narration, and where the sentence carried a fact it
   * has moved into a column header or onto the figure itself. It survives for
   * the panels whose body is an explanation rather than data.
   */
  description?: ReactNode
  /**
   * Controls belonging to this panel, on the right of the header row. A button
   * here rather than a flex row hand-rolled per screen, which is what four of
   * them were doing at four slightly different gaps.
   *
   * Present with no `title`, the actions still sit right, so a panel of
   * controls over a flush table does not need a spacer element to push them
   * over.
   */
  actions?: ReactNode
  footer?: ReactNode
  /**
   * Drops the body's padding, for content that should meet the border: a
   * table, a list of rows, a chart that bleeds.
   */
  flush?: boolean
  /**
   * `tinted` washes the header band in the accent and rules it off from the
   * body. It marks the one panel on a screen that should be read first, which
   * on a queue-led console is a real distinction and not decoration.
   *
   * `variant` and not `tone`, deliberately. `tone` in this library is the
   * five-name status vocabulary, and a tinted header reports no status: a
   * reader who took the wash for a warning would have been told something the
   * panel does not mean.
   */
  variant?: 'plain' | 'tinted'
  /**
   * `section` by default, which with the title below makes this a landmark a
   * screen reader can list and jump to. Pass `div` for a panel that is
   * decoration rather than structure, so the landmark list does not fill up
   * with boxes.
   */
  as?: 'section' | 'div' | 'article'
  children: ReactNode
}

export function Panel({
  title,
  description,
  actions,
  footer,
  flush = false,
  variant = 'plain',
  as: Component = 'section',
  className,
  children,
  ...rest
}: PanelProps) {
  const titleId = useId()
  const panelClassName = [styles.panel, className].filter(Boolean).join(' ')
  const headerClassName = [styles.header, variant === 'tinted' ? styles.tinted : '']
    .filter(Boolean)
    .join(' ')

  return (
    <Component
      className={panelClassName}
      {...(title && Component === 'section' ? { 'aria-labelledby': titleId } : {})}
      {...rest}
    >
      {title || actions ? (
        <header className={headerClassName}>
          <div className={styles.headings}>
            {title ? (
              <h2 className={styles.title} id={titleId}>
                {title}
              </h2>
            ) : null}
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </header>
      ) : null}

      <div className={flush ? styles.bodyFlush : styles.body}>{children}</div>

      {footer ? <footer className={styles.footer}>{footer}</footer> : null}
    </Component>
  )
}

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  /**
   * Lifts on hover and takes a pointer, for a card that is a link to somewhere.
   * The card itself is not the link: put a real anchor inside it. A div with an
   * onClick cannot be opened in a new tab, cannot be copied as a URL and does
   * not tell a screen reader it goes anywhere.
   */
  interactive?: boolean
}

export function Card({ children, interactive = false, className, ...rest }: CardProps) {
  const cardClassName = [styles.card, interactive ? styles.interactive : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cardClassName} {...rest}>
      {children}
    </div>
  )
}
